import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ServiceTypeName } from '#components/order/ServiceTypeName.tsx';
import ErrorFallback from '#components/shared/ErrorFallback.tsx';
import { StopTimeWindow } from '#components/shared/StopInfo.tsx';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import { getOrderIcon, isServiceOrder, type Order } from '#domain/Order.ts';

import { OrderListFilters } from './OrderListFilters.tsx';
import { useOrderListFilters } from './useOrderListFilters.ts';

type OpenOrdersListProps = {
    orders: Order[];
    isOpen?: boolean;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    selectedOrderIds: string[];
    onSelectedOrderIdsChange: (selectedOrderIds: string[]) => void;
};

type OpenOrderCardProps = {
    order: Order;
    selected: boolean;
    onSelectedChange: (selected: boolean) => void;
};

const OpenOrderCard = ({ order, selected, onSelectedChange }: OpenOrderCardProps) => {
    const intl = useIntl();
    const serviceOrder = isServiceOrder(order);
    const primaryStop = serviceOrder ? order.executionStop : order.loadingStop;
    const destination = serviceOrder ? undefined : order.unloadingStop.location.city;
    const checkboxId = `tour-order-${order.id}`;

    return (
        <div
            className={`position-relative width-100pct list-group-item ${selected ? 'bg-highlight-lightest' : 'bg-white'}`}
            data-testid='open-order-card'
        >
            <div className='display-flex align-items-center gap-10'>
                <div
                    className='display-flex flex-column gap-5 min-width-0 flex-1-1-0'
                    data-testid='open-order-card-details'
                >
                    <div className='text-size-14 text-color-darker line-height-18 ellipsis-1'>
                        <span
                            className={`uikit-icon ${getOrderIcon(order)} margin-right-5`}
                            title={intl.formatMessage({
                                id: serviceOrder
                                    ? 'intl-msg:ordercom-cloud-frontend.order.type.service'
                                    : 'intl-msg:ordercom-cloud-frontend.order.type.transport',
                            })}
                        />
                        <span className='text-bold'>
                            {serviceOrder ? (
                                <ServiceTypeName value={order.number} category={order.category} />
                            ) : (
                                order.number
                            )}
                        </span>
                    </div>
                    <div className='text-color-dark ellipsis-1'>
                        {primaryStop.location.country} - {primaryStop.location.zipCode} {primaryStop.location.city}
                        {destination && ` → ${destination}`}
                    </div>
                    {!serviceOrder && (
                        <div className='text-size-12 text-color-dark display-flex flex-column gap-5'>
                            {(
                                [
                                    ['pickup', order.loadingStop],
                                    ['delivery', order.unloadingStop],
                                ] as const
                            ).map(([kind, stop]) => (
                                <div key={kind} className='display-flex align-items-start gap-5'>
                                    <span
                                        className={`label label-${kind === 'pickup' ? 'default' : 'info'} label-condensed flex-shrink-0`}
                                    >
                                        {intl.formatMessage({
                                            id: `intl-msg:ordercom-cloud-frontend.tours.create-dialog.order-card.${kind}`,
                                        })}
                                    </span>
                                    <span>{[stop.location.city, stop.location.street].filter(Boolean).join(', ')}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className='text-size-12 text-color-dark'>
                        <StopTimeWindow from={primaryStop.startTarget} to={primaryStop.endTarget} />
                    </div>
                </div>
                <span className='width-20 flex-shrink-0' aria-hidden='true'>
                    {selected && <span className='uikit-icon icon-ok-sign text-color-primary' />}
                </span>
            </div>
            <input
                type='checkbox'
                id={checkboxId}
                checked={selected}
                className='position-absolute top-0 right-0 bottom-0 left-0 width-100pct height-100pct margin-0 cursor-pointer'
                style={{ opacity: 0 }}
                onChange={event => onSelectedChange(event.target.checked)}
            />
            <label
                aria-hidden='true'
                className='position-absolute top-0 right-0 bottom-0 left-0 cursor-pointer'
                data-testid='open-order-card-click-target'
                htmlFor={checkboxId}
            />
        </div>
    );
};

export const OpenOrdersList = ({
    orders,
    selectedOrderIds,
    onSelectedOrderIdsChange,
    isOpen = true,
    isLoading,
    isError,
    onRetry,
}: OpenOrdersListProps) => {
    const intl = useIntl();
    const showServiceOrders = useFeatureToggle('showServiceOrders', false);
    const filters = useOrderListFilters(orders, isOpen, showServiceOrders);
    const selectedOrderIdSet = useMemo(() => new Set(selectedOrderIds), [selectedOrderIds]);
    const sortedOrders = useMemo(
        () =>
            [...filters.visibleOrders].sort(
                (first, second) => Number(selectedOrderIdSet.has(second.id)) - Number(selectedOrderIdSet.has(first.id))
            ),
        [filters.visibleOrders, selectedOrderIdSet]
    );
    const title = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.orders',
        defaultMessage: 'Orders',
    });

    return (
        <section className='height-100pct min-height-300 display-flex flex-column overflow-y-auto' aria-label={title}>
            <div className='display-flex justify-content-between align-items-center gap-10 margin-bottom-10'>
                <h4 className='margin-0'>{title}</h4>
                <span className='text-color-gray'>
                    {intl.formatMessage(
                        {
                            id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.selected-count',
                            defaultMessage: '{selectedAmount} selected',
                        },
                        { selectedAmount: selectedOrderIds.length }
                    )}
                </span>
            </div>
            <OrderListFilters filters={filters} showServiceOrders={showServiceOrders} />
            {isLoading ? (
                <Spinner />
            ) : isError ? (
                <ErrorFallback onRetry={onRetry} />
            ) : sortedOrders.length === 0 ? (
                <div className='padding-20 text-center text-color-gray border rounded'>
                    {intl.formatMessage({
                        id: orders.length
                            ? 'intl-msg:ordercom-cloud-frontend.tours.order-filters.no-matches'
                            : 'intl-msg:ordercom-cloud-frontend.orders.empty-state.title',
                    })}
                </div>
            ) : (
                <div className='min-height-150 overflow-y-auto flex-1-1' data-testid='open-orders-scroll-list'>
                    <div className='list-group border rounded shadow-subtle'>
                        {sortedOrders.map(order => (
                            <OpenOrderCard
                                key={order.id}
                                order={order}
                                selected={selectedOrderIdSet.has(order.id)}
                                onSelectedChange={selected =>
                                    onSelectedOrderIdsChange(
                                        selected
                                            ? [...selectedOrderIds, order.id]
                                            : selectedOrderIds.filter(orderId => orderId !== order.id)
                                    )
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};
